import React, { useEffect, useState, useRef } from 'react';

import axios from 'axios';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faEye,
  faEyeSlash,
  faLink,
  faLock,
  faSearch,
  faShareAlt,
  faCopy,
  faCamera,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { Html5Qrcode } from 'html5-qrcode';

import LogIn from './LogIn';
import { CobaltMark, HeaderLogo, PRACTICE_INSIGHTS } from './Brand';
import LoginProgress from './LoginProgress';
import LinkExpired, {
  LINK_EXPIRED_STEP,
  isLinkTokenExpired,
} from './LinkExpired';

function LinkPageSingle({
  vendorEntity,
  emrs,
  token,
  onSuccess,
  onClose,
  baseURL,
  mode,
  hasExistingMFA,
  variant,
}) {
  const practice = variant === PRACTICE_INSIGHTS;
  const brandName = practice ? 'Practice Insights' : vendorEntity.name;
  const [step, setStep] = useState(1);
  const [selectedEmr, setSelectedEmr] = useState(emrs[0]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [credentialsReady, setCredentialsReady] = useState(false);
  const [emrEmail, setEmrEmail] = useState(null);
  const [emrPassword, setEmrPassword] = useState(null);
  const [emrDomain, setEmrDomain] = useState(null);
  const [emrOtp, setEmrOtp] = useState('');
  const [emrSecurityAnswer, setEmrSecurityAnswer] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [useExistingMFA, setUseExistingMFA] = useState(false);
  const [showMFAChoice, setShowMFAChoice] = useState(false);
  const [practiceFusionPhone, setPracticeFusionPhone] = useState(null);
  const [practiceFusionOtp, setPracticeFusionOtp] = useState(null);
  const [
    practiceFusionPhoneSetupComplete,
    setPracticeFusionPhoneSetupComplete,
  ] = useState(false);
  const [phoneNumberCopied, setPhoneNumberCopied] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const scannerRef = useRef(null);
  const filteredEmrs = emrs.filter((emr) =>
    emr.emrName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const [imagesLoaded, setImagesLoaded] = useState(false);

  // TODO: consolidate with LinkPageDouble - image preloading, consent screen, EMR selection, loading spinners, and error screens are duplicated
  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    const imgUrls = [];

    if (!isDevelopment && vendorEntity.logoUrl && !practice) {
      imgUrls.push(vendorEntity.logoUrl);
    }

    const imgPromises = imgUrls.map((imgUrl) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = imgUrl;
        img.onload = resolve;
        img.onerror = reject;
      });
    });

    Promise.all(imgPromises)
      .then(() => setImagesLoaded(true))
      .catch((err) => {
        console.log('Failed to load images', err);
        if (isDevelopment) {
          setImagesLoaded(true);
        }
      });
  }, [vendorEntity.logoUrl, practice]);

  useEffect(() => {
    if (credentialsReady) {
      handleSubmit();
      setCredentialsReady(false); // Reset the flag
    }
  }, [credentialsReady, emrEmail, emrPassword]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!imagesLoaded) {
    return null; // or a loading spinner
  }

  // Function to handle the transition from step 1 based on mode
  const handleContinueFromStep1 = async () => {
    // Check if Practice Fusion EMR - need to fetch phone number
    if (selectedEmr && selectedEmr.emrName === 'PracticeFusion') {
      // If we already have the phone number, go directly to step 9
      if (selectedEmr.verificationPhoneNumber) {
        setPracticeFusionPhone(selectedEmr.verificationPhoneNumber);
        setStep(9);
        return;
      }

      // Otherwise, show loading screen and fetch the phone number
      setStep(12); // Go to phone setup loading screen

      try {
        var axiosInstance = axios.create({ baseURL: baseURL });
        const response = await axiosInstance.post(
          '/link/phone-setup',
          {
            token: token,
          },
          {
            withCredentials: true,
          },
        );

        if (response.data.success && response.data.verificationPhoneNumber) {
          setPracticeFusionPhone(response.data.verificationPhoneNumber);
          setStep(9);
        } else {
          setError('Failed to set up phone number. Please try again.');
          setStep(5);
        }
      } catch (error) {
        console.error('Phone number setup failed:', error);
        if (isLinkTokenExpired(error)) {
          setError(null);
          setStep(LINK_EXPIRED_STEP);
          return;
        }
        setError('Failed to set up phone number. Please try again.');
        setStep(5);
      }
      return;
    }

    switch (mode) {
      case 'credentials_required':
        setStep(3);
        break;
      case 'mfa_required':
        setStep(6);
        break;
      case 'add_credential':
        setStep(3); // Go directly to credential entry
        break;
      case 'new':
      default:
        setStep(3); // Proceed to the next logical step or customize as needed
        break;
    }
  };

  const handleErrorResponse = (error) => {
    const errorCode = error.response?.data?.error;

    if (isLinkTokenExpired(error)) {
      setError(null);
      setStep(LINK_EXPIRED_STEP);
      return;
    }

    const OTP_INVALID_CODES = [
      'otp_secret_invalid',
      'invalid otp',
      'invalid_otp',
      'invalid 2FA',
      'invalid 2FA code',
    ];
    const OTP_MISSING_CODES = [
      'otp_secret_missing',
      'missing otp',
      '2FA required but no OTP secret provided',
    ];

    if (errorCode === 'invalid credentials') {
      setError(
        'The credentials you provided are not valid. Please go back and try again.',
      );
      setStep(5);
    } else if (errorCode === 'network_error') {
      setError(
        'There was a problem connecting to the EMR. Please try again. If the problem persists, please contact support.',
      );
      setStep(5);
    } else if (OTP_INVALID_CODES.includes(errorCode)) {
      setError(
        "The authenticator secret key you provided is not valid. Make sure you're entering the secret key itself (a longer string of letters and numbers), not a temporary code from your authenticator app.",
      );
      setStep(7);
    } else if (errorCode === 'security_answer_required') {
      // The EMR login hit an Okta security question we don't have an answer for.
      // Show the question and collect the answer, then resubmit (same round-trip
      // pattern as a missing MFA secret).
      setSecurityQuestion(error.response?.data?.securityQuestion || '');
      setError(null);
      setStep(13);
    } else if (errorCode === 'security_answer_invalid') {
      setError("That answer didn't match. Please try again.");
      setStep(13);
    } else if (OTP_MISSING_CODES.includes(errorCode)) {
      console.log('MFA error - hasExistingMFA:', hasExistingMFA);
      if (hasExistingMFA) {
        console.log('Setting showMFAChoice to true');
        setShowMFAChoice(true);
        setError(null);
        setStep(6); // Show MFA choice step
      } else {
        setError('Please enter your authenticator secret key.');
        setStep(6); // New step for OTP input
      }
    } else if (errorCode === '2FA_setup_required') {
      setError(
        'SMS 2FA setup is still required in Practice Fusion. Please make sure you have added the phone number and set up SMS 2FA in Practice Fusion, then try again.',
      );
      setStep(5);
    } else if (errorCode === 'authenticator_app_not_configured') {
      setError(
        'This account is configured to use SMS or email for two-factor authentication, which is not supported. Please ask your administrator to set up an authenticator app (such as Google Authenticator or Authy), then try again.',
      );
      setStep(5);
    } else if (errorCode === 'ip_not_authorized') {
      setError(
        'This EMR account restricts logins to approved IP addresses. Please contact support for assistance.',
      );
      setStep(5);
    } else if (errorCode === 'password expired') {
      setError(
        'Your EMR password has expired. Please log in to the EMR directly and update your password, then try again.',
      );
      setStep(5);
    } else if (errorCode === 'otp_already_used') {
      setError(
        'The authentication code was already used. Please wait for a new code to generate and try again.',
      );
      setStep(5);
    } else if (errorCode === 'okta_login_failed') {
      setError(
        'The credentials you provided are not valid. Please go back and try again.',
      );
      setStep(5);
    } else if (errorCode === 'okta_sso_unexpected_state') {
      setError(
        'Single sign-on completed but redirected to an unexpected state. Please contact support if this persists.',
      );
      setStep(5);
    } else if (errorCode === 'Timed out waiting for verification code') {
      setError(
        'The verification code was not received in time. Please try again.',
      );
      setStep(5);
    } else if (errorCode === '2fa_failed') {
      setError('Two-factor authentication failed. Please try again.');
      setStep(5);
    } else if (errorCode === 'Failed to authenticate after maximum retries') {
      setError(
        'Authentication failed after the maximum number of retries. Please try again later or contact support.',
      );
      setStep(5);
    } else if (errorCode === 'microsoft_sso_redirect_failed') {
      setError('Microsoft single sign-on redirect failed. Please try again.');
      setStep(5);
    } else if (errorCode === 'microsoft_sso_exception') {
      setError(
        'An error occurred during Microsoft single sign-on. Please contact support if this persists.',
      );
      setStep(5);
    } else if (errorCode === 'unexpected redirect') {
      setError(
        'Authentication was redirected to an unexpected page. Please contact support.',
      );
      setStep(5);
    } else {
      setError('An unexpected error occurred. Please try again.');
      setStep(5);
    }
  };

  const handleCredentialsSubmit = (email, password, domain) => {
    setEmrEmail(email);
    setEmrPassword(password);
    setEmrDomain(domain || null);
    setCredentialsReady(true); // Set the flag to trigger useEffect
  };

  const handlePracticeFusionOtpRequest = async () => {
    try {
      var axiosInstance = axios.create({ baseURL: baseURL });
      const response = await axiosInstance.post(
        '/link/otp',
        {
          token: token,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data.success && response.data.otp) {
        setPracticeFusionOtp(response.data.otp);
        setStep(10); // Go to OTP display step
      } else {
        setError('Failed to generate OTP. Please try again.');
        setStep(11); // Go to Practice Fusion specific error step
      }
    } catch (error) {
      console.error('Practice Fusion OTP request failed:', error);
      if (isLinkTokenExpired(error)) {
        setError(null);
        setStep(LINK_EXPIRED_STEP);
        return;
      }
      setError('Failed to generate OTP. Please try again.');
      setStep(11); // Go to Practice Fusion specific error step
    }
  };

  const extractPhoneDigits = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove +1 and any non-digit characters to get just the 10-digit number
    const digits = phoneNumber.replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const handleCopyPhoneNumber = async () => {
    const phoneDigits = extractPhoneDigits(practiceFusionPhone);
    try {
      await navigator.clipboard.writeText(phoneDigits);
      setPhoneNumberCopied(true);
      setTimeout(() => setPhoneNumberCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy phone number:', err);
    }
  };

  const parseOtpAuthUri = (uri) => {
    try {
      const url = new URL(uri);

      // Standard otpauth:// format (from individual account QR codes)
      if (url.protocol === 'otpauth:') {
        const secret = url.searchParams.get('secret');
        return secret;
      }

      // Google Authenticator export format: otpauth-migration://offline?data=...
      if (url.protocol === 'otpauth-migration:') {
        const data = url.searchParams.get('data');
        if (data) {
          try {
            // Data is URL-encoded, then base64 encoded
            const decodedData = decodeURIComponent(data);
            const binaryString = atob(decodedData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const secrets = extractSecretsFromMigrationData(bytes);
            if (secrets.length > 0) {
              return secrets[0];
            }
          } catch (e) {
            console.error('Error decoding migration data:', e);
          }
        }
      }

      return null;
    } catch (e) {
      console.error('Error parsing OTP URI:', e);
      return null;
    }
  };

  // Extract secrets from Google Authenticator migration protobuf data
  const extractSecretsFromMigrationData = (bytes) => {
    const secrets = [];
    try {
      // MigrationPayload structure:
      // - field 1 (repeated): OtpParameters messages
      // OtpParameters structure:
      // - field 1: secret (bytes)
      // - field 2: name (string)
      // - field 3: issuer (string)
      // etc.

      let i = 0;
      while (i < bytes.length) {
        const tag = bytes[i];
        const fieldNumber = tag >> 3;
        const wireType = tag & 0x07;
        i++;

        if (i >= bytes.length) break;

        if (wireType === 2) {
          // Length-delimited
          // Read varint length
          let length = 0;
          let shift = 0;
          while (i < bytes.length) {
            const b = bytes[i];
            i++;
            length |= (b & 0x7f) << shift;
            if ((b & 0x80) === 0) break;
            shift += 7;
          }

          if (fieldNumber === 1) {
            // This is an OtpParameters message - parse it to find the secret
            const otpParamsEnd = i + length;
            while (i < otpParamsEnd) {
              const innerTag = bytes[i];
              const innerFieldNumber = innerTag >> 3;
              const innerWireType = innerTag & 0x07;
              i++;

              if (innerWireType === 2) {
                // Length-delimited
                let innerLength = 0;
                let innerShift = 0;
                while (i < otpParamsEnd) {
                  const b = bytes[i];
                  i++;
                  innerLength |= (b & 0x7f) << innerShift;
                  if ((b & 0x80) === 0) break;
                  innerShift += 7;
                }

                if (
                  innerFieldNumber === 1 &&
                  innerLength > 0 &&
                  innerLength <= 32
                ) {
                  // This is the secret field
                  const secretBytes = bytes.slice(i, i + innerLength);
                  const base32Secret = bytesToBase32(secretBytes);
                  console.log(
                    'Found secret, length:',
                    innerLength,
                    'base32:',
                    base32Secret,
                  );
                  secrets.push(base32Secret);
                }
                i += innerLength;
              } else if (innerWireType === 0) {
                // Varint
                while (i < otpParamsEnd && (bytes[i] & 0x80) !== 0) i++;
                i++;
              } else {
                // Skip unknown wire types
                break;
              }
            }
            i = otpParamsEnd; // Make sure we're at the end of this OtpParameters
          } else {
            i += length; // Skip other fields
          }
        } else if (wireType === 0) {
          // Varint
          while (i < bytes.length && (bytes[i] & 0x80) !== 0) i++;
          i++;
        } else {
          // Unknown wire type, try to continue
          i++;
        }
      }
    } catch (e) {
      console.error('Error extracting secrets:', e);
    }
    return secrets;
  };

  // Convert bytes to base32 (RFC 4648)
  const bytesToBase32 = (bytes) => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let result = '';
    let bits = 0;
    let value = 0;

    for (let i = 0; i < bytes.length; i++) {
      value = (value << 8) | bytes[i];
      bits += 8;

      while (bits >= 5) {
        bits -= 5;
        result += alphabet[(value >> bits) & 0x1f];
      }
    }

    if (bits > 0) {
      result += alphabet[(value << (5 - bits)) & 0x1f];
    }

    return result;
  };

  const startScanner = async () => {
    setScannerError(null);
    setShowScanner(true);

    // Wait for the DOM element to be available
    setTimeout(async () => {
      try {
        const html5QrCode = new Html5Qrcode('qr-reader');
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            console.log(
              'QR Code scanned:',
              decodedText.substring(0, 100) + '...',
            );
            // Check if it's an otpauth URI
            const secret = parseOtpAuthUri(decodedText);
            if (secret) {
              console.log('Secret extracted successfully');
              setEmrOtp(secret);
              stopScanner();
            } else {
              console.log('Failed to parse QR code');
              setScannerError(
                "Could not extract secret from QR code. Try scanning a single account's QR code, or manually enter the secret key.",
              );
            }
          },
          (errorMessage) => {
            // Ignore scan errors (no QR found in frame)
          },
        );
      } catch (err) {
        console.error('Failed to start scanner:', err);
        if (err.toString().includes('Permission')) {
          setScannerError(
            'Camera permission was denied. Please allow camera access and try again.',
          );
        } else if (
          err.toString().includes('NotFoundError') ||
          err.toString().includes('no camera')
        ) {
          setScannerError(
            'No camera found. Please ensure your device has a camera.',
          );
        } else {
          setScannerError(
            'Failed to start camera. Please try again or enter the secret manually.',
          );
        }
      }
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current
        .stop()
        .catch((err) => console.error('Error stopping scanner:', err));
      scannerRef.current = null;
    }
    setShowScanner(false);
    setScannerError(null);
  };

  const looksLikeOtpCode = (value) => {
    const trimmed = value.trim();
    return /^\d{4,8}$/.test(trimmed);
  };

  const handleSubmit = async () => {
    if (emrOtp && !useExistingMFA && looksLikeOtpCode(emrOtp)) {
      setError(
        'It looks like you entered a temporary code from your authenticator app. Please enter the secret key instead -- this is usually a longer string of letters and numbers (e.g. JBSWY3DPEHPK3PXP).',
      );
      return;
    }

    setStep(4);

    // Give time to show the loading screen
    // await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      var axiosInstance = axios.create({ baseURL: baseURL });

      const response = await axiosInstance.post(
        `/link/submit`,
        {
          emr_email: emrEmail,
          emr_password: emrPassword,
          domain_name: emrDomain,
          emr_otp_secret: emrOtp,
          emr_security_answer: emrSecurityAnswer,
          emr_id: selectedEmr.id,
          token: token,
          override_existing_secret: !useExistingMFA,
        },
        {
          withCredentials: true,
        },
      );
      console.log('server response sucess: ', response);

      if (response.data.success) {
        onSuccess && onSuccess(response.data.token);
        setStep(8);

        // If onSuccess is null that means the Link component is running in a separate window so
        // we need to redirect to the vendor callback so they get the token.
        // if (!onSuccess) {
        //   // Give time to show the loading screen
        //   await new Promise(resolve => setTimeout(resolve, 4000));

        //   const callbackUrl = new URL(vendorEntity.callbackUrl);
        //   callbackUrl.searchParams.append('token', response.data.token);
        //   window.location.href = callbackUrl.href;
        // }
      }
    } catch (error) {
      console.error('server response error: ', error);
      setError(
        'The credentials you provided are not valid. Please go back and try again.',
      );
      handleErrorResponse(error); // Handle different errors
      // setStep(5);
    }
  };

  return (
    <div
      className={`cobalt-flex cobalt-items-center cobalt-justify-center cobalt-min-h-screen cobalt-bg-gray-400 cobalt-bg-opacity-90`}
    >
      <div
        className={`cobalt-pl-7 cobalt-pr-7 cobalt-pt-4 cobalt-pb-8 cobalt-w-360 cobalt-h-[85vh] cobalt-bg-white cobalt-rounded cobalt-shadow-lg cobalt-flex cobalt-flex-col cobalt-rounded-lg`}
      >
        {step === 1 && practice && (
          <>
            <div
              className={`cobalt-flex cobalt-justify-center cobalt-mb-6 cobalt-mt-10`}
            >
              <CobaltMark size={72} />
            </div>
            <h1
              className={`cobalt-embedded-h1 cobalt-mx-auto cobalt-mb-10 cobalt-text-center cobalt-font-light`}
            >
              Connect{' '}
              <span className={`cobalt-font-semibold`}>eClinicalWorks</span> to
              Practice Insights
            </h1>
            <div className={`cobalt-w-10/12 cobalt-mx-auto`}>
              <h2 className={`cobalt-embedded-h2 cobalt-mb-1`}>
                <FontAwesomeIcon icon={faLock} className={`cobalt-mr-1`} />{' '}
                Secure
              </h2>
              <p className={`cobalt-embedded-p cobalt-mb-4 cobalt-ml-6`}>
                Your login is encrypted and used only to sync your data.
              </p>
              <h2 className={`cobalt-embedded-h2 cobalt-mb-1`}>
                <FontAwesomeIcon icon={faEyeSlash} className={`cobalt-mr-1`} />{' '}
                Read only
              </h2>
              <p className={`cobalt-embedded-p cobalt-mb-4 cobalt-ml-6`}>
                Practice Insights reads your data. It never changes anything in
                eClinicalWorks.
              </p>
            </div>
            <div className={`cobalt-mt-auto`}>
              <hr className={`cobalt-border-gray-200 cobalt-mb-4`} />
              <p
                className={`cobalt-embedded-p cobalt-mb-6 cobalt-text-center cobalt-text-sm cobalt-text-gray-600`}
              >
                By selecting "Continue" you agree to the{' '}
                <a
                  href="https://www.usecobalt.com/end-user-privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`cobalt-font-semibold cobalt-underline cobalt-text-gray-600`}
                >
                  Cobalt End User Privacy Policy
                </a>
              </p>
              <button
                onClick={handleContinueFromStep1}
                className={`cobalt-p-2 cobalt-w-full cobalt-bg-primary cobalt-text-white cobalt-rounded cobalt-h-12 hover:cobalt-bg-primary-dark cobalt-transition-colors cobalt-duration-200`}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === 1 && !practice && (
          <>
            <div
              className={`cobalt-flex cobalt-justify-center cobalt-space-x-4 cobalt-mb-8 cobalt-mt-8 cobalt-items-center`}
            >
              <img
                src={vendorEntity.logoUrl}
                alt={`${vendorEntity.name} Logo`}
                className={`cobalt-w-24 cobalt-h-24 cobalt-object-contain`}
              />
              <FontAwesomeIcon
                icon={faShareAlt}
                className={`cobalt-self-center cobalt-text-gray-500`}
              />
              <CobaltMark size={80} />
            </div>
            <h1
              className={`cobalt-embedded-h1 cobalt-mx-auto cobalt-mb-12 cobalt-text-center cobalt-font-light`}
            >
              {vendorEntity.name} uses{' '}
              <span className={`cobalt-font-semibold`}>Cobalt</span> to connect
              your data
            </h1>
            <div className={`cobalt-w-10/12 cobalt-mx-auto`}>
              <h2 className={`cobalt-embedded-h2 cobalt-mb-1`}>
                <FontAwesomeIcon icon={faLock} className={`cobalt-mr-1`} />{' '}
                Secure
              </h2>
              <p className={`cobalt-embedded-p cobalt-mb-4 cobalt-ml-6`}>
                Your data is handled at industry-leading standards.
              </p>
              <h2 className={`cobalt-embedded-h2 cobalt-mb-1`}>
                <FontAwesomeIcon icon={faEyeSlash} className={`cobalt-mr-1`} />{' '}
                Private
              </h2>
              <p className={`cobalt-embedded-p cobalt-mb-4 cobalt-ml-6`}>
                Your data will never be shared without your permission.
              </p>
            </div>
            <div className={`cobalt-mt-auto`}>
              <hr className={`cobalt-border-gray-200 cobalt-mb-4`} />
              <p
                className={`cobalt-embedded-p cobalt-mb-6 cobalt-text-center cobalt-text-sm cobalt-text-gray-600`}
              >
                By selecting "Continue" you agree to the{' '}
                <a
                  href="https://www.usecobalt.com/end-user-privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`cobalt-font-semibold cobalt-underline cobalt-text-gray-600`}
                >
                  Cobalt End User Privacy Policy
                </a>
              </p>
              <button
                onClick={handleContinueFromStep1}
                className={`cobalt-p-2 cobalt-w-full cobalt-bg-primary cobalt-text-white cobalt-rounded cobalt-h-12 hover:cobalt-bg-primary-dark cobalt-transition-colors cobalt-duration-200`}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div
              className={`cobalt-flex cobalt-items-center cobalt-justify-between cobalt-p-4`}
            >
              <button
                onClick={() => setStep(1)}
                className={`cobalt-text-gray-500`}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <HeaderLogo variant={variant} size={24} />
            </div>
            <h2
              className={`cobalt-embedded-h2 cobalt-text-left cobalt-text-xl cobalt-font-semibold cobalt-my-4 cobalt-pl-4`}
            >
              Select your EMR
            </h2>
            <div className={`cobalt-w-full cobalt-p-0 cobalt-relative`}>
              <FontAwesomeIcon
                icon={faSearch}
                className={`cobalt-text-gray-500 cobalt-absolute cobalt-left-3 cobalt-top-1/2 cobalt-transform -cobalt-translate-y-1/2`}
              />
              <input
                type="text"
                placeholder="Search"
                className={`cobalt-pl-10 cobalt-p-2 cobalt-border cobalt-rounded cobalt-w-full`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className={`cobalt-divide-y cobalt-divide-gray-200`}>
              {filteredEmrs.map((emr) => (
                <button
                  key={emr.emrName}
                  className={`cobalt-flex cobalt-items-center cobalt-p-4 cobalt-w-full cobalt-text-left hover:cobalt-cursor-pointer hover:cobalt-bg-gray-100`}
                  onClick={() => {
                    setSelectedEmr(emr);
                    setStep(3);
                  }}
                >
                  <div
                    className={`cobalt-flex-shrink-0 cobalt-w-12 cobalt-h-6 cobalt-mr-4`}
                  >
                    <img
                      src={emr.emrIconUrl}
                      alt={`${emr.emrName} Logo`}
                      className={`cobalt-h-full cobalt-w-full cobalt-object-contain`}
                    />
                  </div>
                  <div>
                    <p className="cobalt-font-semibold">{emr.emrName}</p>
                    <p className="cobalt-text-gray-600 cobalt-text-xs cobalt-font-thin">
                      {emr.emrInstanceDomain}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 3 && (
          <LogIn
            variant={variant}
            entity={selectedEmr}
            handleSubmit={handleCredentialsSubmit}
            setStep={setStep}
            prevStep={1}
          />
        )}

        {step === 4 && (
          <>
            <div className={`cobalt-flex cobalt-justify-center cobalt-mb-6`}>
              <HeaderLogo variant={variant} size={18} />
            </div>
            {practice ? (
              <LoginProgress emrName={selectedEmr.emrName} />
            ) : (
              <div
                className={`cobalt-mt-4 cobalt-flex cobalt-flex-col cobalt-items-center cobalt-justify-center cobalt-h-full`}
              >
                <div
                  className={`cobalt-loader cobalt-ease-linear cobalt-rounded-full cobalt-border-4 cobalt-border-t-4 cobalt-border-gray-200 cobalt-h-12 cobalt-w-12`}
                ></div>
                <p
                  className={`cobalt-embedded-p cobalt-mt-2 cobalt-embedded-p cobalt-mt-2 cobalt-text-center`}
                >
                  Logging in...
                </p>
              </div>
            )}
          </>
        )}

        {step === 5 && (
          <>
            <div className="cobalt-flex cobalt-items-left">
              <button
                onClick={() => setStep(3)}
                className="cobalt-text-center cobalt-mb-8"
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className="cobalt-self-center cobalt-text-gray-500"
                />
              </button>
            </div>
            <div className="cobalt-flex cobalt-flex-col cobalt-items-center cobalt-justify-center cobalt-h-2/3">
              <h1 className="cobalt-embedded-h1 cobalt-w-5/6 cobalt-mb-4">{`Log in to ${selectedEmr.emrName} failed.`}</h1>
              <div className="cobalt-w-5/6">
                <p className="cobalt-embedded-p">{error}</p>
              </div>
              <br />
              {/* <div className="cobalt-w-5/6">
              <p className="cobalt-embedded-p">If you need assistance please contact bryan@usecobalt.com</p>
            </div> */}
            </div>
          </>
        )}

        {step === LINK_EXPIRED_STEP && <LinkExpired onClose={onClose} />}

        {step === 6 && (
          <>
            <div
              className={`cobalt-flex cobalt-flex-col cobalt-items-start cobalt-mt-12 cobalt-mb-4`}
            >
              <div
                className={`cobalt-flex cobalt-flex-row cobalt-items-start cobalt-mb-12`}
              >
                <div
                  className={`cobalt-flex-shrink-0 cobalt-mr-2 ${selectedEmr.emrName === 'OncoEMR' ? 'cobalt-h-16' : 'cobalt-h-10'}`}
                >
                  <img
                    src={selectedEmr.logoUrl || selectedEmr.emrLogoUrl}
                    alt={`${selectedEmr.emrName} Logo`}
                    className="cobalt-h-full cobalt-w-full cobalt-object-contain"
                  />
                </div>
              </div>
              {showMFAChoice ? (
                <h1 className="cobalt-text-left cobalt-font-normal">
                  {selectedEmr.emrName} requires MFA. You have an existing MFA
                  secret stored.
                </h1>
              ) : (
                <h1 className="cobalt-text-left cobalt-font-normal">
                  Enter your authenticator secret key
                </h1>
              )}
            </div>
            <div className="cobalt-flex cobalt-flex-col cobalt-items-centers">
              {showMFAChoice ? (
                <>
                  <div className="cobalt-mb-6 cobalt-w-full">
                    <div className="cobalt-flex cobalt-items-center cobalt-mb-4">
                      <input
                        type="radio"
                        id="useExisting"
                        name="mfaChoice"
                        checked={useExistingMFA}
                        onChange={() => setUseExistingMFA(true)}
                        className="cobalt-mr-3 cobalt-w-4 cobalt-h-4"
                      />
                      <label htmlFor="useExisting" className="cobalt-text-sm">
                        Use my existing MFA secret
                      </label>
                    </div>
                    <div className="cobalt-flex cobalt-items-center cobalt-mb-4">
                      <input
                        type="radio"
                        id="enterNew"
                        name="mfaChoice"
                        checked={!useExistingMFA}
                        onChange={() => setUseExistingMFA(false)}
                        className="cobalt-mr-3 cobalt-w-4 cobalt-h-4"
                      />
                      <label htmlFor="enterNew" className="cobalt-text-sm">
                        Enter a new MFA secret
                      </label>
                    </div>
                  </div>
                  {!useExistingMFA && (
                    <>
                      <div className="cobalt-relative cobalt-w-full cobalt-mb-2">
                        <input
                          type={showSecret ? 'text' : 'password'}
                          placeholder="Enter your authenticator secret key..."
                          value={emrOtp}
                          onChange={(e) => {
                            setEmrOtp(e.target.value);
                            setError(null);
                          }}
                          className="cobalt-p-2 cobalt-pr-10 cobalt-border cobalt-rounded cobalt-w-full"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecret(!showSecret)}
                          className="cobalt-absolute cobalt-right-3 cobalt-top-1/2 cobalt-transform -cobalt-translate-y-1/2 cobalt-text-gray-500 hover:cobalt-text-gray-700"
                        >
                          <FontAwesomeIcon
                            icon={showSecret ? faEyeSlash : faEye}
                          />
                        </button>
                      </div>
                      <p className="cobalt-text-xs cobalt-text-gray-600 cobalt-mb-4 cobalt-text-center">
                        Need help finding your secret key?{' '}
                        <a
                          href="https://github.com/krissrex/google-authenticator-exporter"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cobalt-text-blue-600 cobalt-underline"
                        >
                          Learn how to export it
                        </a>
                        .
                      </p>

                      <div className="cobalt-flex cobalt-items-center cobalt-w-full cobalt-mb-4">
                        <div className="cobalt-flex-1 cobalt-h-px cobalt-bg-gray-300"></div>
                        <span className="cobalt-px-3 cobalt-text-gray-500 cobalt-text-sm">
                          or
                        </span>
                        <div className="cobalt-flex-1 cobalt-h-px cobalt-bg-gray-300"></div>
                      </div>

                      <button
                        onClick={startScanner}
                        className="cobalt-p-2 cobalt-w-full cobalt-border cobalt-border-gray-300 cobalt-rounded cobalt-h-12 cobalt-mb-4 cobalt-flex cobalt-items-center cobalt-justify-center cobalt-text-gray-700 hover:cobalt-bg-gray-50 cobalt-transition-colors cobalt-duration-200"
                      >
                        <FontAwesomeIcon
                          icon={faCamera}
                          className="cobalt-mr-2"
                        />
                        Scan QR Code
                      </button>
                    </>
                  )}
                  {error && (
                    <p className="cobalt-text-sm cobalt-text-red-600 cobalt-mb-3">
                      {error}
                    </p>
                  )}
                  <button
                    onClick={handleSubmit}
                    disabled={!useExistingMFA && !emrOtp}
                    className={`cobalt-p-2 cobalt-w-full cobalt-bg-primary cobalt-text-white cobalt-rounded cobalt-h-12 ${useExistingMFA || emrOtp ? 'hover:cobalt-bg-primary-dark cobalt-transition-colors cobalt-duration-200' : 'disabled:cobalt-opacity-50'}`}
                  >
                    {useExistingMFA
                      ? 'Continue with Existing Secret'
                      : 'Save New Secret Key'}
                  </button>
                </>
              ) : (
                <>
                  <div className="cobalt-relative cobalt-w-full cobalt-mb-2">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      placeholder="Enter your authenticator secret key..."
                      value={emrOtp}
                      onChange={(e) => {
                        setEmrOtp(e.target.value);
                        setError(null);
                      }}
                      className="cobalt-p-2 cobalt-pr-10 cobalt-border cobalt-rounded cobalt-w-full"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="cobalt-absolute cobalt-right-3 cobalt-top-1/2 cobalt-transform -cobalt-translate-y-1/2 cobalt-text-gray-500 hover:cobalt-text-gray-700"
                    >
                      <FontAwesomeIcon icon={showSecret ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  <p className="cobalt-text-xs cobalt-text-gray-600 cobalt-mb-4 cobalt-text-center">
                    Need help finding your secret key?{' '}
                    <a
                      href="https://github.com/krissrex/google-authenticator-exporter"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cobalt-text-blue-600 cobalt-underline"
                    >
                      Learn how to export it
                    </a>
                    .
                  </p>

                  <div className="cobalt-flex cobalt-items-center cobalt-w-full cobalt-mb-4">
                    <div className="cobalt-flex-1 cobalt-h-px cobalt-bg-gray-300"></div>
                    <span className="cobalt-px-3 cobalt-text-gray-500 cobalt-text-sm">
                      or
                    </span>
                    <div className="cobalt-flex-1 cobalt-h-px cobalt-bg-gray-300"></div>
                  </div>

                  <button
                    onClick={startScanner}
                    className="cobalt-p-2 cobalt-w-full cobalt-border cobalt-border-gray-300 cobalt-rounded cobalt-h-12 cobalt-mb-4 cobalt-flex cobalt-items-center cobalt-justify-center cobalt-text-gray-700 hover:cobalt-bg-gray-50 cobalt-transition-colors cobalt-duration-200"
                  >
                    <FontAwesomeIcon icon={faCamera} className="cobalt-mr-2" />
                    Scan QR Code
                  </button>

                  {error && (
                    <p className="cobalt-text-sm cobalt-text-red-600 cobalt-mb-3">
                      {error}
                    </p>
                  )}
                  <button
                    onClick={handleSubmit}
                    disabled={!emrOtp}
                    className={`cobalt-p-2 cobalt-w-full cobalt-bg-primary cobalt-text-white cobalt-rounded cobalt-h-12 ${emrOtp ? 'hover:cobalt-bg-primary-dark cobalt-transition-colors cobalt-duration-200' : 'disabled:cobalt-opacity-50'}`}
                  >
                    Save Secret Key
                  </button>
                </>
              )}
            </div>

            {/* QR Scanner Modal */}
            {showScanner && (
              <div className="cobalt-scanner-overlay">
                <div className="cobalt-scanner-container">
                  <div className="cobalt-flex cobalt-justify-between cobalt-items-center cobalt-mb-4">
                    <h2 className="cobalt-text-lg cobalt-font-semibold">
                      Scan QR Code
                    </h2>
                    <button
                      onClick={stopScanner}
                      className="cobalt-text-gray-500 hover:cobalt-text-gray-700 cobalt-p-2"
                    >
                      <FontAwesomeIcon icon={faTimes} size="lg" />
                    </button>
                  </div>
                  <div id="qr-reader" className="cobalt-qr-reader"></div>
                  {scannerError && (
                    <div className="cobalt-mt-4 cobalt-p-3 cobalt-bg-red-50 cobalt-border cobalt-border-red-200 cobalt-rounded cobalt-text-red-700 cobalt-text-sm">
                      {scannerError}
                    </div>
                  )}
                  <p className="cobalt-text-center cobalt-text-gray-600 cobalt-text-sm cobalt-mt-4">
                    Point your camera at your authenticator app's export QR code
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {step === 7 && (
          <>
            <div className="cobalt-flex cobalt-items-left">
              <button
                onClick={() => setStep(6)}
                className="cobalt-text-center cobalt-mb-8"
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className="cobalt-self-center cobalt-text-gray-500"
                />
              </button>
            </div>
            <div className="cobalt-flex cobalt-flex-col cobalt-items-center cobalt-justify-center cobalt-h-2/3">
              <h1 className="cobalt-embedded-h1 cobalt-w-5/6 cobalt-mb-4">{`Log in to ${selectedEmr.emrName} failed.`}</h1>
              <div className="cobalt-w-5/6">
                <p className="cobalt-embedded-p">{error}</p>
              </div>
              <br />
              {/* <div className="cobalt-w-5/6">
                <p className="cobalt-embedded-p">If you need assistance please contact bryan@usecobalt.com</p>
              </div> */}
            </div>
          </>
        )}

        {step === 8 && (
          <>
            <div className="cobalt-flex cobalt-flex-col cobalt-items-center cobalt-justify-center cobalt-h-2/3">
              <div className="cobalt-flex cobalt-justify-center cobalt-space-x-4 cobalt-mb-8 cobalt-mt-8 cobalt-items-center">
                <img
                  src={selectedEmr.emrLogoUrl}
                  alt={`${selectedEmr.emrName} Logo`}
                  className="cobalt-w-24 cobalt-h-24 cobalt-object-contain"
                />
                <FontAwesomeIcon
                  icon={faLink}
                  className="cobalt-self-center cobalt-text-gray-500"
                />
                {practice ? (
                  <CobaltMark size={96} />
                ) : (
                  <img
                    src={vendorEntity.logoUrl}
                    alt={`${vendorEntity.name} Logo`}
                    className="cobalt-w-24 cobalt-h-24 cobalt-object-contain"
                  />
                )}
              </div>
              <h1 className="cobalt-embedded-h1 cobalt-mb-4">
                {mode === 'add_credential'
                  ? 'Credential added successfully!'
                  : 'Account successfully linked!'}
              </h1>
              <div className="cobalt-text-center cobalt-w-full cobalt-mb-6">
                {mode === 'add_credential' ? (
                  <>
                    <p className="cobalt-embedded-p cobalt-mb-2">
                      Your new credential has been added to your account
                    </p>
                    <p className="cobalt-embedded-p">
                      You can close this window
                    </p>
                  </>
                ) : (
                  <>
                    <p className="cobalt-embedded-p cobalt-mb-2">
                      Your data will now be synchronized between{' '}
                      {selectedEmr.emrName} and {brandName}
                    </p>
                    <p className="cobalt-embedded-p">
                      You can close this window
                    </p>
                  </>
                )}
              </div>
              <div className="cobalt-mt-4">
                {onSuccess && (
                  <button
                    onClick={onClose}
                    className="cobalt-p-2 cobalt-bg-blue-500 cobalt-text-white cobalt-rounded hover:cobalt-bg-blue-700 cobalt-transition-colors cobalt-duration-200"
                  >
                    Close
                  </button>
                )}
              </div>
              {/* <div className="mt-4">
                <a href="http://localhost:3001?redirect=true" className="text-blue-500 underline">Back to {vendorEntity.name}</a>
              </div> */}
            </div>
          </>
        )}

        {step === 9 && (
          <>
            <div
              className={`cobalt-flex cobalt-items-center cobalt-justify-between cobalt-p-4`}
            >
              <button
                onClick={() => setStep(1)}
                className="cobalt-text-gray-500"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <HeaderLogo variant={variant} size={24} />
            </div>
            <div
              className={`cobalt-flex cobalt-flex-col cobalt-items-start cobalt-mt-12 cobalt-mb-4`}
            >
              <div
                className={`cobalt-flex cobalt-flex-row cobalt-items-start cobalt-mb-12`}
              >
                <div className="cobalt-flex-shrink-0 cobalt-mr-2">
                  <img
                    src={selectedEmr.logoUrl || selectedEmr.emrLogoUrl}
                    alt={`${selectedEmr.emrName} Logo`}
                    className="cobalt-h-10 cobalt-w-auto cobalt-object-contain"
                  />
                </div>
              </div>
              <h1 className="cobalt-text-left cobalt-font-normal cobalt-mb-6">
                Practice Fusion Setup Required
              </h1>
            </div>
            <div className="cobalt-flex cobalt-flex-col cobalt-items-center">
              <div className="cobalt-mb-6 cobalt-p-4 cobalt-bg-blue-50 cobalt-border-l-4 cobalt-border-blue-400 cobalt-rounded-r cobalt-w-full">
                <p className="cobalt-text-sm cobalt-text-blue-800 cobalt-leading-relaxed cobalt-mb-4">
                  <span className="cobalt-font-semibold">Step 1:</span> You need
                  to set up SMS 2FA in Practice Fusion using this phone number:
                </p>
                <div className="cobalt-bg-white cobalt-p-3 cobalt-rounded cobalt-border cobalt-text-center cobalt-mb-4">
                  <div className="cobalt-flex cobalt-items-center cobalt-justify-center cobalt-space-x-3">
                    <span className="cobalt-font-mono cobalt-text-lg cobalt-font-semibold">
                      {practiceFusionPhone}
                    </span>
                    <button
                      onClick={handleCopyPhoneNumber}
                      className={`cobalt-flex cobalt-items-center cobalt-justify-center cobalt-w-8 cobalt-h-8 cobalt-rounded cobalt-transition-colors cobalt-duration-200 ${
                        phoneNumberCopied
                          ? 'cobalt-bg-green-100 cobalt-text-green-600'
                          : 'cobalt-text-primary hover:cobalt-bg-gray-100'
                      }`}
                      style={!phoneNumberCopied ? { color: '#576de' } : {}}
                      title={
                        phoneNumberCopied ? 'Copied!' : 'Copy 10-digit number'
                      }
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                </div>
                <p className="cobalt-text-xs cobalt-text-blue-700 cobalt-leading-relaxed">
                  Follow this guide:{' '}
                  <a
                    href="https://help.practicefusion.com/s/article/How-do-I-complete-a-verification-phone-number-reset"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cobalt-underline cobalt-text-blue-600 hover:cobalt-text-blue-800"
                  >
                    Complete a verification phone number reset in Practice
                    Fusion
                  </a>
                </p>
              </div>
              <div className="cobalt-flex cobalt-items-center cobalt-mb-4 cobalt-w-full">
                <input
                  type="checkbox"
                  id="phoneSetupComplete"
                  checked={practiceFusionPhoneSetupComplete}
                  onChange={(e) =>
                    setPracticeFusionPhoneSetupComplete(e.target.checked)
                  }
                  className="cobalt-mr-3 cobalt-w-4 cobalt-h-4 cobalt-text-primary cobalt-border-gray-300 cobalt-rounded"
                />
                <label
                  htmlFor="phoneSetupComplete"
                  className="cobalt-text-sm cobalt-text-gray-700"
                >
                  I've added the phone number in Practice Fusion
                </label>
              </div>
              <button
                onClick={handlePracticeFusionOtpRequest}
                disabled={!practiceFusionPhoneSetupComplete}
                className={`cobalt-p-2 cobalt-w-full cobalt-rounded cobalt-h-12 cobalt-text-sm cobalt-transition-colors cobalt-duration-200 ${
                  practiceFusionPhoneSetupComplete
                    ? 'cobalt-bg-primary cobalt-text-white hover:cobalt-bg-primary-dark'
                    : 'cobalt-bg-gray-300 cobalt-text-gray-500 cobalt-cursor-not-allowed'
                }`}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === 10 && (
          <>
            <div
              className={`cobalt-flex cobalt-items-center cobalt-justify-between cobalt-p-4`}
            >
              <button
                onClick={() => setStep(9)}
                className="cobalt-text-gray-500"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <HeaderLogo variant={variant} size={24} />
            </div>
            <LogIn
              variant={variant}
              entity={selectedEmr}
              handleSubmit={handleCredentialsSubmit}
              setStep={setStep}
              prevStep={9}
              practiceFusionOtp={practiceFusionOtp}
              hideHeader={true}
            />
          </>
        )}

        {step === 11 && (
          <>
            <div className="cobalt-flex cobalt-items-left">
              <button
                onClick={() => setStep(9)}
                className="cobalt-text-center cobalt-mb-8"
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className="cobalt-self-center cobalt-text-gray-500"
                />
              </button>
            </div>
            <div className="cobalt-flex cobalt-flex-col cobalt-items-center cobalt-justify-center cobalt-h-2/3">
              <h1 className="cobalt-embedded-h1 cobalt-w-5/6 cobalt-mb-4">
                Failed to generate OTP
              </h1>
              <div className="cobalt-w-5/6">
                <p className="cobalt-embedded-p">{error}</p>
              </div>
              <br />
            </div>
          </>
        )}

        {step === 12 && (
          <>
            <div className={`cobalt-flex cobalt-justify-center cobalt-mb-6`}>
              <HeaderLogo variant={variant} size={18} />
            </div>
            <div
              className={`cobalt-mt-4 cobalt-flex cobalt-flex-col cobalt-items-center cobalt-justify-center cobalt-h-full`}
            >
              <div
                className={`cobalt-loader cobalt-ease-linear cobalt-rounded-full cobalt-border-4 cobalt-border-t-4 cobalt-border-gray-200 cobalt-h-12 cobalt-w-12`}
              ></div>
              <p className={`cobalt-embedded-p cobalt-mt-4 cobalt-text-center`}>
                Setting up phone number for SMS
                <span className="cobalt-inline-block cobalt-w-6 cobalt-text-left cobalt-ml-1">
                  <span className="cobalt-dot-1">.</span>
                  <span className="cobalt-dot-2">.</span>
                  <span className="cobalt-dot-3">.</span>
                </span>
              </p>
            </div>
          </>
        )}

        {step === 13 && (
          <>
            <div
              className={`cobalt-flex cobalt-flex-col cobalt-items-start cobalt-mt-12 cobalt-mb-4`}
            >
              <div
                className={`cobalt-flex cobalt-flex-row cobalt-items-start cobalt-mb-12`}
              >
                <div
                  className={`cobalt-flex-shrink-0 cobalt-mr-2 ${selectedEmr.emrName === 'OncoEMR' ? 'cobalt-h-16' : 'cobalt-h-10'}`}
                >
                  <img
                    src={selectedEmr.logoUrl || selectedEmr.emrLogoUrl}
                    alt={`${selectedEmr.emrName} Logo`}
                    className="cobalt-h-full cobalt-w-full cobalt-object-contain"
                  />
                </div>
              </div>
              <h1 className="cobalt-text-left cobalt-font-normal">
                {selectedEmr.emrName} is asking a security question.
              </h1>
            </div>
            <div className="cobalt-flex cobalt-flex-col cobalt-items-centers">
              {securityQuestion && (
                <p className="cobalt-text-left cobalt-font-medium cobalt-mb-2">
                  {securityQuestion}
                </p>
              )}
              {error && (
                <p className="cobalt-text-red-500 cobalt-text-sm cobalt-mb-2">
                  {error}
                </p>
              )}
              <input
                type="password"
                placeholder="Your answer"
                value={emrSecurityAnswer}
                onChange={(e) => setEmrSecurityAnswer(e.target.value)}
                className="cobalt-mb-2 cobalt-p-2 cobalt-border cobalt-rounded cobalt-w-full"
              />
              <button
                onClick={handleSubmit}
                disabled={!emrSecurityAnswer}
                className={`cobalt-p-2 cobalt-w-full cobalt-bg-primary cobalt-text-white cobalt-rounded cobalt-h-12 ${emrSecurityAnswer ? 'hover:cobalt-bg-primary-dark cobalt-transition-colors cobalt-duration-200' : 'disabled:cobalt-opacity-50'}`}
              >
                Submit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LinkPageSingle;
